import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiUsers, FiCalendar, FiClock, FiSmile } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../contexts/AuthContext';
import { hapticSelection } from '../services/hapticsService';

function BottomNavigation({ forceShow = false }) {
  const location = useLocation();
  const { user } = useAuth();

  // Don't show bottom nav on login/signup pages, the onboarding wizard, or
  // when the user is not authenticated. `forceShow` lets the onboarding done
  // step opt back in to the regular nav as a soft hand-off into the app.
  if (!user || ['/login', '/signup', '/email-confirmation', '/forgot-password', '/auth/callback'].includes(location.pathname)) {
    return null;
  }
  if (!forceShow && (location.pathname.startsWith('/onboarding') || location.pathname === '/welcome')) {
    return null;
  }

  const navItems = [
    {
      path: '/rounds',
      icon: FiCalendar,
      label: 'Rounds',
      exactMatch: false
    },
    {
      path: '/friends',
      icon: FiSmile,
      label: 'Friends',
      exactMatch: false
    },
    {
      path: '/groups',
      icon: FiUsers,
      label: 'Groups',
      exactMatch: false
    },
    {
      path: '/my-availability',
      icon: FiClock,
      label: 'Availability',
      exactMatch: false
    }
  ];

  const isActiveTab = (item) => {
    if (item.exactMatch) {
      return location.pathname === item.path;
    }
    return location.pathname.startsWith(item.path);
  };

  return (
    <div className="md:hidden">
      {/* Spacer to prevent content from being hidden behind bottom nav.
          Tracks the nav's actual footprint (~70px on web) plus safe-area-inset
          on mobile. */}
      <div style={{ height: 'calc(72px + env(safe-area-inset-bottom))' }} />
      
      {/* Bottom Navigation */}
      <motion.div
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        className="fixed bottom-0 left-0 right-0 bg-surface border-t border-border-subtle shadow-xs z-50 safe-area-inset-bottom"
      >
        <div className="flex items-center justify-around px-2 py-2">
          {navItems.map((item) => {
            const isActive = isActiveTab(item);
            const ItemWrapper = Link;
            const wrapperProps = {
              to: item.path,
              onClick: () => {
                if (!isActive) hapticSelection();
              },
            };

            return (
              <ItemWrapper
                key={item.path}
                {...wrapperProps}
                className={`
                  relative flex flex-col items-center justify-center min-h-[44px] px-3 py-2 rounded-lg
                  transition-all duration-200 min-w-[60px] max-w-[80px] flex-1
                  ${isActive
                    ? 'text-accent'
                    : 'text-text-muted hover:text-text-secondary active:text-accent'
                  }
                `}
              >
                {/* Active Background */}
                {isActive && (
                  <motion.div
                    layoutId="bottomNavActiveTab"
                    className="absolute inset-0 bg-accent/10 rounded-lg"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}

                {/* Icon and Label */}
                <div className="relative z-10 flex flex-col items-center space-y-1">
                  <motion.div
                    
                    transition={{ duration: 0.1 }}
                  >
                    <SafeIcon
                      icon={item.icon}
                      className={`w-5 h-5 ${isActive ? 'text-accent' : ''}`}
                    />
                  </motion.div>
                  
                  <span className={`
                    text-xs font-medium leading-none text-center
                    ${isActive ? 'text-accent' : ''}
                  `}>
                    {item.label}
                  </span>
                </div>

                {/* Active Indicator Dot */}
                {isActive && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1 w-1.5 h-1.5 bg-accent rounded-full"
                  />
                )}
              </ItemWrapper>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
}

export default BottomNavigation;